<script setup>
import { computed, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinanceInstallmentContextHeader from '../components/RealEstateFinanceInstallmentContextHeader.vue';
import RealEstateFinanceTransferForm from '../components/RealEstateFinanceTransferForm.vue';
import { useRealEstateBrokerCommissionTransfer } from '../composables/useRealEstateBrokerCommissionTransfer';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const salesTransactionId = computed(() => route.params.salesTransactionId);
const installmentId = computed(() => route.params.installmentId);
const beneficiaryType = computed(() => route.params.beneficiaryType);
const beneficiaryId = computed(() => route.params.beneficiaryId);
const requestId = computed(() => route.params.requestId);
const state = useRealEstateBrokerCommissionTransfer({
  beneficiaryId,
  beneficiaryType,
  installmentId,
  requestId,
  notify: useAlert,
  t,
});

const accountParams = computed(() => ({ accountId: route.params.accountId }));
const goToFinance = () =>
  router.push({
    name: 'operacao_imobiliaria_financeiro',
    params: accountParams.value,
  });
const goBack = () => {
  if (!salesTransactionId.value) return goToFinance();

  return router.push({
    name: 'operacao_imobiliaria_financeiro_status',
    params: {
      accountId: route.params.accountId,
      installmentId: installmentId.value,
      salesTransactionId: salesTransactionId.value,
    },
  });
};
const submit = async () => {
  if (await state.submit()) goBack();
};
const beneficiaryMissing = computed(() =>
  Boolean(state.detail.value && !state.beneficiary.value)
);
let redirectTimeout;

watch(
  beneficiaryMissing,
  isMissing => {
    clearTimeout(redirectTimeout);
    if (isMissing) redirectTimeout = setTimeout(goBack, 3000);
  },
  { immediate: true }
);

onBeforeUnmount(() => clearTimeout(redirectTimeout));
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('REAL_ESTATE_FINANCE.TRANSFER.PAGE_TITLE')"
      :subtitle="t('REAL_ESTATE_FINANCE.TRANSFER.PAGE_SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_FINANCE.TITLE') },
        { label: t('REAL_ESTATE_FINANCE.TRANSFER.PAGE_TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE')"
      @back="goBack"
    />
    <section class="mx-auto w-full max-w-[1400px] px-4 pb-5 sm:px-6 lg:px-10">
      <RealEstateFinanceInstallmentContextHeader
        :address="state.detail.value?.parcela?.endereco"
        :loading="state.loading.value"
        :process="state.detail.value?.dados_processo"
      />
    </section>
    <main
      class="mx-auto flex w-full max-w-[760px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <div
        v-if="state.loading.value"
        class="h-96 animate-pulse rounded-lais bg-white"
      />
      <p
        v-else-if="state.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ state.error.value }}
      </p>
      <p
        v-else-if="beneficiaryMissing"
        class="rounded-lais border border-lais-border-soft bg-white p-4 text-sm text-lais-text-body"
      >
        {{ t('REAL_ESTATE_FINANCE.TRANSFER.BENEFICIARY_NOT_FOUND_REDIRECT') }}
      </p>
      <RealEstateFinanceTransferForm
        v-else
        :beneficiary="state.beneficiary.value"
        :amount="state.amount.value"
        :transferred-on="state.transferredOn.value"
        :observation="state.observation.value"
        :receipt="state.receipt.value"
        :saving="state.saving.value"
        :progress="state.uploadProgress.value"
        :can-submit="state.canSubmit.value"
        @update:amount="state.amount.value = $event"
        @update:transferred-on="state.transferredOn.value = $event"
        @update:observation="state.observation.value = $event"
        @update:receipt="state.receipt.value = $event"
        @submit="submit"
      />
      <div class="flex justify-end">
        <LaisPrimaryButton variant="ghost" @click="goBack">
          {{ t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE') }}
        </LaisPrimaryButton>
      </div>
    </main>
  </div>
</template>
